import type { IApply, IApplyPatch, IPostApplyOffer } from "@bbj-types/apply";
import type { IAction } from "@bbj-types/applyManagementProcess";
import type { IOffer } from "@bbj-types/offer";
import type { ITemplate } from "@bbj-types/templates";
import type { ObjectId, Override } from "@bbj-types/utils";
import { useCallback } from "react";
import type { ICreateOfferPayload } from "../offers";
import { axios, axiosV2 } from "./axios";

interface IGetAppliesBody {
  kind: "job" | "social";
  candidate: ObjectId;
  applicable: boolean;
  processed: boolean;
  status: string[];
  sort?: {
    updatedAt?: number;
    scoring?: number;
    travelTime?: number;
  };
  limit: number;
}

export interface IGetApplies {
  count: number;
  data: IApply[];
  limit: number;
  skip: number;
  total: number;
}

export const useGetApplys = () => {
  const [state, execute, cancel] = axios<IGetApplies, IGetAppliesBody, unknown>(
    {
      url: "/applys/search",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: IGetAppliesBody) => {
      return execute({
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export interface IPostBatchAppliesBody {
  ids: ObjectId[];
  action: string;
  status: string;
  currentAction: IAction;
}

export const usePostBatchApplies = () => {
  const [state, execute, cancel] = axios<IApply[], IPostBatchAppliesBody, unknown>(
    {
      url: "/applys/batch",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: IPostBatchAppliesBody) => {
      return execute({
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const usePatchApply = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/applys",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string, data: Override<Partial<IApply>, { offer: Partial<IOffer> }> | Partial<IApply>) => {
      return execute({
        url: `/applys/${id}`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const usePatchApplyV2 = () => {
  const [state, execute, cancel] = axios<{ offerId?: string }>(
    {
      url: "/v2/applys",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string, data: IApplyPatch) => {
      return execute({
        url: `/v2/applys/${id}`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const usePostApply = () => {
  const [state, execute, cancel] = axios<{ apply: IApply }, IPostApplyOffer, unknown>(
    {
      url: "/applys",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: IPostApplyOffer) => {
      return execute({
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export interface IApplyOfferPost extends Omit<IOffer, "job" | "skills"> {
  job: ObjectId[] | undefined;
  skills:
    | {
        competence: ObjectId;
        requirement: string;
      }[]
    | undefined;
}
export interface IApplyPost extends Omit<IApply, "_id" | "offer" | "source"> {
  offer: IApplyOfferPost;
}
interface IPostBatchAppliesByTypeBody {
  applies: IApplyPost[];
}
interface IPostBatchAppliesByTypeData {
  nbInserted: number;
  appliesId: string[];
}

export const usePostBatchAppliesByType = () => {
  const [state, execute, cancel] = axios<IPostBatchAppliesByTypeData>(
    {
      url: "/applys/batch",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (type: string, data: IPostBatchAppliesByTypeBody) => {
      return execute({
        url: `/applys/batch/${type}`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

interface IPatchBatchAppliesByTypeBody {
  applies: IApply[];
  update: Partial<IApply>;
}
interface IPatchBatchAppliesByTypeData {
  nbModified: number;
  appliesId: string[];
}

export const usePatchBatchAppliesByType = () => {
  const [state, execute, cancel] = axios<IPatchBatchAppliesByTypeData>(
    {
      url: "/applys/batch",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (type: string, data: IPatchBatchAppliesByTypeBody) => {
      const { applies, update } = data;
      const appliesIds = applies.map((apply) => apply._id);
      return execute({
        url: `/applys/batch/${type}`,
        data: {
          appliesIds,
          update,
        },
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

interface IPostAppliesBatchCandidatesBody {
  offer: IOffer;
  candidatesSMS: string[];
  candidatesEmail: string[];
  smsTemplate: Partial<ITemplate>;
  emailTemplate: Partial<ITemplate>;
  managementProcessId: string;
  statusSMS: string;
  statusEmail: string;
}

export const usePostAppliesBatchCandidates = () => {
  const [state, execute, cancel] = axiosV2<undefined>(
    {
      url: "/applys/batch/candidates",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: IPostAppliesBatchCandidatesBody) => {
      return execute({
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

interface IUseGetAppliesFromOfferBody {
  data: IApply[];
  limit: number;
  skip: number;
  total: number;
  count: number;
}

export const useGetAppliesFromOffer = () => {
  const [state, execute, cancel] = axiosV2<IUseGetAppliesFromOfferBody>(
    {
      url: "/applys/offer",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string, organisation: string, skip?: number, limit?: number, authors?: string[]) => {
      return execute({
        url: `/applys/offer/${id}`,
        params: {
          organisation,
          skip,
          limit,
          authors,
        },
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export interface IPostApplyAndOffer {
  source: "manual" | "auto";
  candidate: ObjectId;
  managementProcess: ObjectId;
  kind: "job" | "social";
  status: string;
  isFollowUpGenerated?: boolean;
  contractStartDate?: string;
  contractEndDate?: string;
  itwDate?: Date;
  offer: ICreateOfferPayload;
}

export const usePostApplyAndOffer = () => {
  const [state, execute, cancel] = axiosV2<IApply>(
    {
      url: "/applys/offer",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: IPostApplyAndOffer) => {
      return execute({
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};
export * from "./fup";
export * from "./postAppliesBatchOffer";
export * from "./getCandidateSocialApplies";
export * from "./getSocialApply";
export * from "./patchSocialApplies";
export * from "./postSocialApplies";
export * from "./batchPatchSocialApply";
export * from "./types";
